import React, { createContext, useContext, useState, useEffect } from 'react';
import { UrlContext } from './UrlContext';

// 1. Create the context
const UserContext = createContext();

// 2. Create the provider component
export const UserProvider = ({ children }) => {

  const { apiBaseUrl } = useContext(UrlContext);

  const [user, setUser] = useState(null);

  // Load user data from localStorage on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && typeof parsedUser === 'object') {
          setUser(parsedUser);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      localStorage.removeItem('user');
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data);
        try {
          localStorage.setItem('user', JSON.stringify(data));
        } catch (error) {
          console.error('Error saving user data:', error);
        }
        return { success: true, user: data };
      } else {
        return { 
          success: false, 
          error: data.error || 'Login failed' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Unable to connect to the server. Please try again later.' 
      };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data);
        try {
          localStorage.setItem('user', JSON.stringify(data));
        } catch (error) {
          console.error('Error saving user data:', error);
        }
        return { success: true, user: data };
      }

      return {
        success: false,
        error: data.error || 'Google sign-in failed',
      };
    } catch (error) {
      console.error('Google login error:', error);
      return {
        success: false,
        error: 'Unable to connect to the server. Please try again later.',
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser,
      login,
      loginWithGoogle,
      logout,
      isLoggedIn: !!user,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </UserContext.Provider>
  );
};

// 3. Create the hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
